(ns arthur.export.frames
  "An `Exporter` that writes a numbered PNG sequence and its WAV into one zip.

  THE MASTER FORMAT. Every other export is a re-interpretation of this one: the
  PNGs hold exactly the bytes `raster/draw-ops!` wrote, expanded through the ramp
  at an integer zoom, so nothing between the scanline fill and the file resamples,
  subsamples or smooths. `domain/png` has the argument for why that matters here
  more than it would in most tools.

  IT IS ALSO SMALLER THAN IT SOUNDS, which is worth saying because \"lossless
  frame sequence\" reads as gigabytes. That intuition comes from photographic
  frames — the 1440x1920 source stills this project stopped storing were 112MB for
  7.6 seconds. This is nine palette colours of flat fill at 320x200, upscaled by
  an integer: a frame's entropy is on the order of kilobytes, and the zoom is
  nearly free because a duplicated scanline filters to zeros. The finished master
  of a take runs comparable to the lossy PROXY of the footage it came from.

  ONE ARCHIVE, PICTURE AND SOUND TOGETHER, rather than two downloads. They have
  to stay in sync all the way to the cutting room, and a second `<a download>`
  click is also the one a browser is most likely to block."
  (:require [arthur.audio.mix :as mix]
            [arthur.domain.png :as png]
            [arthur.domain.zip :as zip]
            [arthur.export :as export]))

(defn- pad
  "Frame numbers are ONE-BASED and zero-padded to a fixed width, because that is
  what an NLE's image-sequence importer looks for: a common stem, a fixed-width
  counter, one extension. Width comes from the frame count, so a 900-frame export
  is `0001`..`0900` and nothing sorts `10` before `9`."
  [i width]
  (let [s (str i)]
    (str (.repeat "0" (max 0 (- width (.-length s)))) s)))

(defn exporter
  "A frame-sequence `Exporter`.

  `at` is the timestamp stamped on every zip entry, defaulting to now. It is a
  parameter so that the same frames produce the same archive byte for byte, which
  is what makes `export.frames-test` able to assert on one."
  ([] (exporter (js/Date.)))
  ([at]
   (let [state (atom nil)]
     (reify export/Exporter
       (begin! [_ {:keys [name width height zoom fps frames ramp audio]}]
         (reset! state
                 {:name    name
                  :ramp    ramp
                  :fps     fps
                  :frames  frames
                  ;; Held once. At zoom 6 the scratch inside it is seven
                  ;; megabytes, which is not a thing to allocate per frame.
                  :encode  (png/encoder width height zoom)
                  :digits  (max 4 (.-length (str frames)))
                  :entries (cond-> []
                             audio (conj {:name (str name "/audio.wav")
                                          :data (mix/wav-bytes audio)}))})
         nil)

       (frame! [_ i raster]
         (let [{:keys [encode ramp name digits]} @state]
           ;; Encoded HERE, inside the frame's turn, because the walk reuses the
           ;; raster: keeping a reference to it and encoding later would encode
           ;; the last frame N times, and every frame would be a valid PNG of the
           ;; wrong picture.
           (-> (encode raster (or (:palette-ramp raster) ramp))
               (.then (fn [bytes]
                        (swap! state update :entries conj
                               {:name (str name "/" (pad (inc i) digits) ".png")
                                :data bytes})
                        nil)))))

       (finish! [_]
         (let [{:keys [name entries]} @state]
           (js/Promise.resolve
            {:filename (str name ".zip")
             :blob     (zip/blob entries at)})))))))
